/* eslint-disable no-else-return,consistent-return */
import { QueryClient } from "@tanstack/react-query"
import compact from "lodash-es/compact"
import concat from "lodash-es/concat"
import find from "lodash-es/find"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import reject from "lodash-es/reject"
import some from "lodash-es/some"
import { DateTime } from "luxon"

import { getSessionModalityEnumFromType } from "@/3514/adapters"
import { ECoachingSessionsGroupQueryKey, ECoachingSessionsIndividualQueryKey } from "@/3514/config"
import {
    ECoachDashboardQueryKey,
    type ICoachSessionWithoutAttendanceMarksDto as ISessionWithoutAttendanceMarksDto
} from "@/3514/pages/coach.dashboard"
import { ESessionModality, type ICoachingSessionDto, type ICoachingSessionsQueryDto } from "@/3514/types"

import uiConfig from "./attendance.modal.ui.config"
import {
    EAttendanceModalOrigin as EModalOrigin,
    type TAttendanceModalFormRowData as TFormRowData,
    type TAttendanceModalProps as TModalProps
} from "./attendance.modal.ui.types"

function _getCoachingSessionRawData({
    qc,
    modality,
    sessionId,
    participantId,
    groupId
}: {
    qc: QueryClient
    modality: ESessionModality
    sessionId: number
    participantId: number
    groupId: number
}): ICoachingSessionDto {
    const rawDataByGroup: ICoachingSessionsQueryDto = qc.getQueryData<ICoachingSessionsQueryDto>(
        ECoachingSessionsGroupQueryKey({ groupId })
    )

    const rawDataByParticipant: ICoachingSessionsQueryDto = qc.getQueryData<ICoachingSessionsQueryDto>(
        ECoachingSessionsIndividualQueryKey({ participantId })
    )

    const queryRawData: ICoachingSessionsQueryDto | undefined =
        modality === ESessionModality.Group
            ? !!rawDataByGroup
                ? rawDataByGroup
                : rawDataByParticipant
            : rawDataByParticipant

    return find<ICoachingSessionDto>(
        compact<ICoachingSessionDto>(
            concat<ICoachingSessionDto>(
                queryRawData.upcoming_sessions,
                queryRawData.passed_sessions,
                queryRawData.live_session
            )
        ),
        { session_id: sessionId }
    )
}

function isEnoughTimeToMarkAttendance(startTimeISO: string, hoursBuffer: number): boolean {
    const startTime: DateTime = DateTime.fromISO(startTimeISO)

    if (!startTime.isValid) return false

    const _hoursUntilStart: number = startTime.diff(DateTime.now(), "hours").hours

    return _hoursUntilStart < 0 ? true : _hoursUntilStart <= hoursBuffer
}
export { isEnoughTimeToMarkAttendance }

export default {
    getHeaderFormattedDate(isoTime: string): string {
        return DateTime.fromISO(isoTime).isValid
            ? DateTime.fromISO(isoTime).toFormat("MMM d, h:mma").replace("AM", "am").replace("PM", "pm")
            : String("")
    },
    getHeaderInfoProps({ modalProps, qc }: { modalProps: TModalProps; qc: QueryClient }) {
        if (modalProps.origin === EModalOrigin.MissedAttendances) {
            const queryData: ISessionWithoutAttendanceMarksDto[] = qc.getQueryData<ISessionWithoutAttendanceMarksDto[]>(
                ECoachDashboardQueryKey.MarkedAttendances()
            )

            const session: ISessionWithoutAttendanceMarksDto = find<ISessionWithoutAttendanceMarksDto>(queryData, {
                session_id: modalProps.sessionId
            })

            return {
                startDate: session.session_time,
                duration: session.duration,
                isGuest: session.is_extra_party,
                modality: getSessionModalityEnumFromType(session.session_modality)
            }
        }

        if (modalProps.origin === EModalOrigin.CoachingSessions) {
            const session: ICoachingSessionDto = _getCoachingSessionRawData({
                qc,
                modality: modalProps.sessionModality,
                sessionId: modalProps.sessionId,
                participantId: modalProps.participantId,
                groupId: modalProps.groupId
            })

            return {
                startDate: session.session_time,
                duration: session.session_type.duration,
                isGuest: session.is_extra_party_session,
                modality: modalProps.sessionModality
            }
        }
    },
    hasUnfilledFreeResponse(marks: TFormRowData[]): boolean {
        return some<TFormRowData>(
            marks,
            (mark: TFormRowData): boolean =>
                includes(uiConfig.TAG_TITLES_WITH_REQUIRED_FREE_RESPONSE, mark.focusTagTitle) &&
                isEmpty(mark.focusTagResponse)
        )
    },
    hasAbsentParticipants(marks: TFormRowData[]): boolean {
        return some<TFormRowData>(reject<TFormRowData>(marks, { isExtraParty: true }), {
            attendance: "not_present"
        })
    }
}
